import { useCallback, useMemo } from "react";
import {
  ActionIcon,
  Menu,
  Modal,
  Text,
  Group,
  Button,
  Divider,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconDotsVertical,
  IconEdit,
  IconMail,
  IconBan,
  IconRefresh,
} from "@tabler/icons-react";
import { PortalAccessGrant } from "../../../services/admin/portalAccess.service";
import {
  useResendPortalAccessInvitation,
  useRestorePortalAccessGrant,
  useRevokePortalAccessGrant,
} from "../../../hooks/admin/usePortalAccess";
import {
  classifyPortalGrantError,
  EXISTING_GRANT_ERROR_FALLBACKS,
} from "../../../utils/portalAccessErrors";

interface PortalGrantActionsMenuProps {
  grant: PortalAccessGrant;
  onEdit: (grant: PortalAccessGrant) => void;
}

function showResendSuccess(grant: PortalAccessGrant): void {
  notifications.show({
    color: "green",
    message: grant.invitationHeldForReview
      ? "Invitation is waiting for review in Pending Emails"
      : "Invitation resent",
  });
}

export function PortalGrantActionsMenu({
  grant,
  onEdit,
}: PortalGrantActionsMenuProps) {
  const [revokeOpened, { open: openRevoke, close: closeRevoke }] =
    useDisclosure(false);
  const revokeGrant = useRevokePortalAccessGrant();
  const restoreGrant = useRestorePortalAccessGrant();
  const resendInvitation = useResendPortalAccessInvitation();

  const handleEdit = useCallback(() => {
    onEdit(grant);
  }, [grant, onEdit]);

  const handleResend = useCallback(() => {
    resendInvitation.mutate(grant.id, {
      onSuccess: showResendSuccess,
      onError: (error) =>
        notifications.show({
          color: "red",
          message: classifyPortalGrantError(
            error,
            EXISTING_GRANT_ERROR_FALLBACKS
          ),
        }),
    });
  }, [grant.id, resendInvitation]);

  const handleRestore = useCallback(() => {
    restoreGrant.mutate(grant.id, {
      onSuccess: () =>
        notifications.show({ color: "green", message: "Access restored" }),
      onError: (error) =>
        notifications.show({
          color: "red",
          message: classifyPortalGrantError(
            error,
            EXISTING_GRANT_ERROR_FALLBACKS
          ),
        }),
    });
  }, [grant.id, restoreGrant]);

  const handleConfirmRevoke = useCallback(() => {
    revokeGrant.mutate(grant.id, {
      onSuccess: () => {
        notifications.show({ color: "green", message: "Access revoked" });
        closeRevoke();
      },
      onError: (error) =>
        notifications.show({
          color: "red",
          message: classifyPortalGrantError(
            error,
            EXISTING_GRANT_ERROR_FALLBACKS
          ),
        }),
    });
  }, [grant.id, revokeGrant, closeRevoke]);

  const canResend = useMemo(() => grant.state === "invited", [grant.state]);
  const canRevoke = useMemo(
    () => grant.state === "invited" || grant.state === "active",
    [grant.state]
  );
  const canRestore = useMemo(() => grant.state === "revoked", [grant.state]);

  const displayName = useMemo(
    () =>
      [grant.firstName, grant.lastName].filter(Boolean).join(" ").trim() ||
      grant.portalIdentity.email,
    [grant.firstName, grant.lastName, grant.portalIdentity.email]
  );

  return (
    <>
      <Menu
        position="bottom-end"
        offset={4}
        withinPortal
        shadow="md"
        width={200}
      >
        <Menu.Target>
          <ActionIcon variant="subtle" aria-label="Grant actions">
            <IconDotsVertical size={16} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item leftSection={<IconEdit size={14} />} onClick={handleEdit}>
            Edit
          </Menu.Item>
          {canResend && (
            <Menu.Item
              leftSection={<IconMail size={14} />}
              onClick={handleResend}
              disabled={resendInvitation.isPending}
            >
              Resend invitation
            </Menu.Item>
          )}
          {canRestore && (
            <Menu.Item
              leftSection={<IconRefresh size={14} />}
              onClick={handleRestore}
              disabled={restoreGrant.isPending}
            >
              Restore access
            </Menu.Item>
          )}
          {canRevoke && (
            <Menu.Item
              color="red"
              leftSection={<IconBan size={14} />}
              onClick={openRevoke}
              disabled={revokeGrant.isPending}
            >
              Revoke access
            </Menu.Item>
          )}
        </Menu.Dropdown>
      </Menu>
      <Modal.Root
        opened={revokeOpened}
        onClose={closeRevoke}
        closeOnClickOutside={false}
        size="sm"
        centered
      >
        <Modal.Overlay />
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>Revoke portal access?</Modal.Title>
            <Modal.CloseButton />
          </Modal.Header>
          <Modal.Body>
            <Text size="sm">
              This will revoke portal access for {displayName} on{" "}
              {grant.subjectName}. They will lose access to reports delivered to
              this subject.
            </Text>
          </Modal.Body>
          <Divider />
          <Group justify="space-between" p="md">
            <Button variant="default" onClick={closeRevoke}>
              Cancel
            </Button>
            <Button
              color="red"
              loading={revokeGrant.isPending}
              onClick={handleConfirmRevoke}
            >
              Revoke
            </Button>
          </Group>
        </Modal.Content>
      </Modal.Root>
    </>
  );
}
